/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import type { UnityDialogProps } from "./UnityDialogProps";
import "./UnityDialog.scss";

const TITLE_ID: string = "unity-dialog-title";
const MESSAGE_ID: string = "unity-dialog-message";

/**
 * UnityDialog — generic, production-grade global dialog/overlay shell.
 *
 * Designed to serve every current and future modal use case in unity-web
 * (alerts, confirmations, error dialogs) behind one shared shell:
 *   - Any number of action buttons, with any label text.
 *   - Responsive width between a min-width and max-width; long text wraps.
 *   - Horizontal, right-aligned action row where each button is sized to its
 *     own text, never wrapping to a second line however many actions are
 *     configured.
 *   - Title and message are each fully optional — supports title-only,
 *     message-only, or actions-only layouts.
 *
 * This component is purely presentational: all state lives in Redux
 * (see viewModel/unityDialog) and is wired up by UnityDialogContainer.
 */
const UnityDialog: FunctionComponent<UnityDialogProps> = (
  props: UnityDialogProps
): JSX.Element => {
  const {
    title,
    message,
    errorCode,
    variant,
    actions,
    focusedActionIndex,
    onAction,
    onFocusAction
  } = props;

  const hasTitle: boolean = title.length > 0;
  const hasMessage: boolean = message.length > 0;
  const hasErrorCode: boolean = errorCode.length > 0;

  const dialogClassName: string =
    "unity-dialog__dialog" +
    (variant === "error" ? " unity-dialog__dialog--error" : "");

  const dialogAriaProps: {
    "aria-labelledby"?: string;
    "aria-describedby"?: string;
  } = {};
  if (hasTitle) {
    dialogAriaProps["aria-labelledby"] = TITLE_ID;
  }
  if (hasMessage) {
    dialogAriaProps["aria-describedby"] = MESSAGE_ID;
  }

  return (
    <div className="unity-dialog">
      <div className="unity-dialog__scrim" />
      <div
        className={dialogClassName}
        role="alertdialog"
        aria-modal="true"
        {...dialogAriaProps}
      >
        {hasTitle && (
          <div className="unity-dialog__title-block">
            <div className="unity-dialog__title-row">
              <h2 id={TITLE_ID} className="unity-dialog__title">
                {title}
              </h2>
              {hasErrorCode && (
                <span className="unity-dialog__error-code">{errorCode}</span>
              )}
            </div>
            <hr className="unity-dialog__separator" />
          </div>
        )}
        {hasMessage && (
          <p id={MESSAGE_ID} className="unity-dialog__message">
            {message}
          </p>
        )}
        {actions.length > 0 && (
          <div className="unity-dialog__actions">
            {actions.map(
              (
                action: { id: string; label: string },
                index: number
              ): JSX.Element => {
                const buttonClassName: string =
                  "unity-dialog__button" +
                  (index === focusedActionIndex
                    ? " unity-dialog__button--focused"
                    : "");
                return (
                  <button
                    key={action.id}
                    type="button"
                    className={buttonClassName}
                    tabIndex={0}
                    onClick={(): void => onAction(action.id)}
                    onMouseEnter={(): void => onFocusAction(index)}
                    onFocus={(): void => onFocusAction(index)}
                  >
                    <span className="unity-dialog__button-label">
                      {action.label}
                    </span>
                  </button>
                );
              }
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnityDialog;
